import React, { useState } from 'react';
import clsx from 'clsx';
import ModalButton from 'components/ModalButton/ModalButton';
import autoRenewConfirmIcon from 'assets/images/icons/auto-renew-confirm.svg';
import CommonLargeModal from 'components/Modals/CommonLargeModal';
import { formatDate } from 'utils/date';
import styles from './OptOutFeedbackModal.module.scss';

export interface OptOutFeedbackOption {
  key: string;
  label: string;
}

export const OPT_OUT_FEEDBACK_OPTIONS: OptOutFeedbackOption[] = [
  { key: 'better_price', label: 'Found a better price elsewhere' },
  { key: 'cover_elsewhere', label: 'Getting cover elsewhere (e.g. bank / insurance) at renewal' },
  { key: 'shop_around', label: 'Want to shop around before my renewal' },
  { key: 'no_longer_need', label: 'Won’t need cover after my renewal date' },
  { key: 'dont_use_enough', label: 'Not using it enough to renew again' },
  { key: 'unhappy_service', label: 'Not happy with the service' },
];

export interface OptOutFeedbackPayload {
  // Labels rather than keys — the keys are a front-end concern only.
  reasons: string[];
  otherText: string;
}

interface OptOutFeedbackModalProps {
  show: boolean;
  onContinue: (_payload: OptOutFeedbackPayload) => void;
  onSkip: () => void;
  onCancel: () => void;
  // Lets the journey hold the modal mounted until its fade-out has played out.
  onExited?: () => void;
  // The member's own renewal date, shown back to them as the cover end date.
  policyEnd?: string;
  // Set once an answer is on its way, so a double press cannot send it twice.
  submitted?: boolean;
  // Set when the last submit attempt failed, so the member knows to retry.
  error?: boolean;
}

const OTHER_TEXT_MAX_LENGTH = 250;

const OptOutFeedbackModal = ({
  show, onContinue, onSkip, onCancel, onExited, policyEnd, submitted = false, error = false,
}: OptOutFeedbackModalProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');

  const toggleOption = (key: string) => {
    setSelected((current) => (current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key]));
  };

  const handleOtherTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOtherText(event.target.value.slice(0, OTHER_TEXT_MAX_LENGTH));
  };

  const formattedPolicyEnd = formatDate(policyEnd, 'd MMMM yyyy');
  const isContinueDisabled = submitted || (selected.length === 0 && otherText.trim().length === 0);

  const handleContinue = () => {
    // Report the labels the member actually read, in the order they were shown.
    // Backend expects selectedOptionIds field but the contract in fact needs labels instead of IDs
    const reasons = OPT_OUT_FEEDBACK_OPTIONS
      .filter((option) => selected.includes(option.key))
      .map((option) => option.label);

    onContinue({ reasons, otherText: otherText.trim() });
  };

  return (
    <CommonLargeModal
      show={show}
      onHide={onCancel}
      onExited={onExited}
      bodyStyle={{ maxWidth: 'none' }}
      scrollable
    >
      <div className={styles.feedbackForm}>
        <img className="mt-1" src={autoRenewConfirmIcon} alt="Auto renewal confirm icon" />
        <h4 className="my-3 fw-bold fs-4" style={{ color: '#47474a', lineHeight: '1' }}>Auto renew is off</h4>
        <p className="fs-6">
          We’ve updated your preference.
          {formattedPolicyEnd && <> Your cover will end on <b>{formattedPolicyEnd}</b>.</>}
          {' '}We’ll get in touch before then with your renewal options.
          Your feedback helps us improve, so please let us know why you’ve chosen to turn off auto-renewal.
        </p>

        <p className="fs-6 fw-bold mb-3" style={{ color: '#47474a' }}>Select all that apply</p>

        <div className="mb-3">
          {OPT_OUT_FEEDBACK_OPTIONS.map((option) => {
          const isSelected = selected.includes(option.key);

          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggleOption(option.key)}
              className={clsx(styles.option, isSelected && styles.optionSelected)}
              data-testid={`opt-out-feedback-option-${option.key}`}
            >
              {option.label}
            </button>
          );
        })}
        </div>

        <div className={styles.otherWrapper}>
          <textarea
            className={styles.otherInput}
            placeholder="Other..."
            value={otherText}
            maxLength={OTHER_TEXT_MAX_LENGTH}
            onChange={handleOtherTextChange}
            rows={3}
            data-testid="opt-out-feedback-other"
          />
          <span className={styles.otherCounter} data-testid="opt-out-feedback-other-counter">
            {otherText.length} / {OTHER_TEXT_MAX_LENGTH}
          </span>
        </div>

        <p className={`${styles.complaint} mb-4`}>
          We’re not able to respond individually to comments left here – to raise a complaint,
          {' '}
          <a href="https://www.rac.co.uk/complaints" target="_blank" rel="noopener noreferrer">contact us here</a>
        </p>

        {error && (
        <p className={styles.error} role="alert" data-testid="opt-out-feedback-error">
          Sorry, we couldn’t submit your feedback. Please try again.
        </p>
      )}

        <ModalButton
          type="button"
          variant="filled"
          onClick={handleContinue}
          disabled={isContinueDisabled}
          data-testid="opt-out-feedback-continue"
        >
          Done
        </ModalButton>

        <ModalButton
          type="button"
          variant="outline"
          onClick={onSkip}
          disabled={submitted}
          data-testid="opt-out-feedback-skip"
        >
          Skip
        </ModalButton>
      </div>
    </CommonLargeModal>
  );
};

export default OptOutFeedbackModal;
